<template>
  <div>
    <div v-if="!isLoaded || !list" class="py-10 flex justify-center">
      <BaseSpinner />
    </div>
    <div v-else-if="isNotEnoughPermissions">
      <div
        class="rounded-xl px-6 py-5 space-y-3 bg-amber-500 text-white text-md flex items-center"
      >
        <BaseIcon name="outline_exclamation" class="mr-2" />
        {{ t('companies.not_enough_permissions') }}
      </div>
    </div>
    <div v-else>
      <div class="bg-white rounded-xl p-4 sm:p-8">
        <BaseLink
          v-if="authStore?.profile?.company?.id"
          :to="`/companies/${authStore?.profile?.company?.id}`"
          class="hover:opacity-80 transition-opacity shrink mr-auto"
        >
          {{ authStore?.profile?.company?.name }}
        </BaseLink>
        <h2 class="text-xl font-bold">
          {{ $t('companies.member_title') }}
        </h2>

        <div class="flex flex-col gap-3 mt-6">
          <template v-for="(user, index) in list?.state.hits" :key="user.id">
            <hr v-if="index" class="dark:border-t-trueGray-500" />
            <div
              class="flex md:items-center gap-3 md:gap-6 md:flex-row flex-col"
              :class="!!isMemberChanging[user.id] && 'pointer-events-none'"
            >
              <NuxtLink
                :to="useLocaleRoute()(`/users/${user.id}`)"
                class="hover:opacity-80 transition-opacity md:basis-[250px] shrink"
                target="_blank"
              >
                <BaseAvatar
                  :title="
                    getFullName(user) +
                    (authStore?.profile?.id === user.id
                      ? ' ' + $t('companies.member_you')
                      : '')
                  "
                  size="lg"
                  :src="getAvatar(user)"
                  :text="user.job || undefined"
                />
              </NuxtLink>

              <BaseSelect
                v-model="user.company_roles"
                name="companyRoles"
                :options="companyRolesOptions"
                class="grow w-full md:w-auto text-black md:basis-[300px] shrink-0"
                :disabled="!!isMemberChanging[user.id]"
                :placeholder="$t('companies.member_role_placeholder')"
                multiple
                :show-clear-button="false"
                @change="onRoleChange(user.id, user.company_roles || [])"
              />
              <BaseButton
                size="xs"
                theme="destructive"
                class="ml-auto md:ml-0"
                :disabled="!!isMemberChanging[user.id]"
                @click="onDeleteMember(user)"
              >
                {{ $t('companies.delete_member_button') }}
              </BaseButton>
            </div>
          </template>
          <div
            v-if="!list?.state?.hits.length"
            class="py-5 dark:text-white flex justify-center"
          >
            <BaseSpinner />
          </div>
          <BaseObserve
            v-if="list?.state?.loaded && !list?.allRecordsLoaded"
            :key="list?.state.hits.length"
            @change="onMembersObserveChange"
          />
        </div>
      </div>
      <BaseConfirm ref="deleteConfirm" @confirmed="onDeleteMemberConfirm">
        <template #default="{ confirmData }">
          {{
            $t('companies.confirm_delete_member_text', {
              name: getFullName(confirmData),
            })
          }}
        </template>
      </BaseConfirm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import { useAuth } from '~/stores/auth'
import { useNotifications } from '~/stores/notifications'
import { useCompanies } from '~/stores/companies'
import BaseConfirm from '~/components/base/BaseConfirm.vue'
import { EmployeeRole } from '~/models/users'
import type { CompanyUser } from '~/models/users'

const { pushNotification } = useNotifications()
const authStore = useAuth()
const { t } = useI18n()
const router = useRouter()
const companiesStore = useCompanies()

type List = ReturnType<typeof useInfinityList<CompanyUser>>
const list = ref(null) as Ref<List | null>
const isNotEnoughPermissions = ref(false)

const isLoaded = ref(false)

onMounted(async () => {
  if (!authStore.profile?.company) {
    router.push(useLocaleRoute()('/users/me/edit/basic') as RouteLocationRaw)
    return
  }

  list.value = companiesStore.initCompanyMembersList(
    authStore.profile.company.id
  )

  try {
    await list.value?.getPage()
  } catch (error: any) {
    if (error.status === 403) {
      isNotEnoughPermissions.value = true
      // router.push(useLocaleRoute()('/users/me/edit/basic') as RouteLocationRaw)
    }
    useLogError(error)
  }

  isLoaded.value = true
})

const deleteConfirm = ref<InstanceType<typeof BaseConfirm> | null>(null)
const isMemberChanging = reactive<{ [key: string]: boolean }>({})
const companyRolesOptions = translateOptions(
  enumToOptions(EmployeeRole, false),
  t,
  'companies.roles'
)

async function onMembersObserveChange(visible: boolean) {
  if (visible) {
    await list.value?.nextPage()
  }
}

async function onRoleChange(id: number, roles: EmployeeRole[]) {
  try {
    isMemberChanging[id] = true

    await companiesStore.setRolesInCompany(
      authStore.profile?.company?.id as number,
      id,
      roles
    )

    pushNotification({
      title: t('notifications.companies.change_member_roles_success'),
    })
  } catch (error: any) {
    pushNotification({
      title: t('notifications.companies.change_member_roles_fail'),
      description:
        error.status === 403
          ? t('notifications.companies.not_enough_permissions')
          : undefined,
      theme: 'destructive',
    })

    useLogError(error)
  } finally {
    isMemberChanging[id] = false
  }
}

function onDeleteMember(member: CompanyUser) {
  deleteConfirm?.value?.open(member)
}

async function onDeleteMemberConfirm(member: CompanyUser) {
  try {
    isMemberChanging[member.id] = true

    await companiesStore.removeMemberFromCompany(
      authStore.profile?.company?.id as number,
      member.id
    )
    if (list.value?.state?.hits) {
      list.value.state.hits = list?.value?.state.hits.filter(
        (item) => item.id !== member.id
      )
    }

    pushNotification({
      title: t('notifications.companies.delete_member_success'),
    })
  } catch (error: any) {
    pushNotification({
      title: t('notifications.companies.delete_member_fail'),
      description:
        error.status === 403
          ? t('notifications.companies.not_enough_permissions')
          : undefined,
      theme: 'destructive',
    })

    useLogError(error)
  } finally {
    isMemberChanging[member.id] = false
  }
}
</script>
